import { GraphDomain } from '../../types/graph.types';
import type { AppConfig } from '../types';

export const SS_CONFIG: AppConfig = {
  id: 'ss',
  apiApp: 'ss',
  domain: GraphDomain.SgsSatsang,
  browseFacetKeys: ['composer'],
  features: {
    bhajanBabiesSection: false,
    bhajanOfTheDay: false,
    todaysBhajans: false,
    collections: false,
    playlists: false,
    distraction: true,
    pronunciation: true,
    appDownload: true,
    projectModePromo: false,
    redirectUnknownToHome: true,
  },
  brand: {
    siteName: 'SGS Satsang',
    wordmark: 'SGS Satsang',
    logoSrc: `${process.env.PUBLIC_URL ?? ''}/img/ss/logo.png`,
    contactEmail: 'sgs.bhajanmala@gmail.com',
    aboutIntro:
      'Beloved bhajans by various artists—songs about Sri Swamiji and timeless devotional favorites.',
  },
  skeleton: { baseColor: '#1A1A1A', highlightColor: '#2A2A2A' },
  browseHeroBackground:
    'radial-gradient(ellipse at 30% 0%, rgba(232,93,117,0.12) 0%, transparent 55%), radial-gradient(ellipse at 80% 30%, rgba(218,50,85,0.10) 0%, transparent 55%), var(--bg-1)',
  storeUrls: {
    ios: 'https://apps.apple.com/us/app/sgs-satsang/id6749831728',
    android: 'https://play.google.com/store/apps/details?id=org.dycusa.sgssatsang',
  },

  /**
   * /download copy. SGS Satsang is a DIFFERENT product from Bhajan Mala, and
   * the copy must not be borrowed from it. Figures come from
   * GET /api/graph/index?platform=web4&a=ss measured 2026-07-23:
   *
   *   102 bhajans (101 with lyrics), across 8 deities — 64 of them Sadguru
   *   Stuti and 15 Guru, which is what "songs about Sri Swamiji" means in the
   *   data; 5 composition languages (Telugu 37, Saṁskṛtaṁ 21, Hindī 5,
   *   Kannaḍa 3, English 1); composer credits on 16, all Śrī Datta
   *   Vijayānanda Tīrtha Svāmījī.
   *
   * Two things bm claims that ss must NOT: only 2 of the 102 bhajans carry an
   * audible edge, so there is no "recordings" story here; and while ragam and
   * talam exist for ~70, they are secondary — this app is browsed by deity,
   * composer and composition language, which is what its Browse screen shows.
   *
   * Screenshots are the ones on the live App Store listing (id6749831728),
   * staged from appSpecific/ss/public/img/app/.
   */
  download: {
    // Rose (#E85D75) rather than bm's amber — ss redefines --accent-gold.
    heroBackground:
      'radial-gradient(ellipse at 15% 0%, rgba(232,93,117,0.14), transparent 55%), radial-gradient(ellipse at 85% 20%, rgba(218,50,85,0.12), transparent 55%), var(--bg-1)',
    closingBackground:
      'radial-gradient(ellipse at 50% 100%, rgba(232,93,117,0.10), transparent 60%)',
    eyebrow: 'for iPhone, iPad, Mac & Android',
    // The app's own tagline, near enough verbatim: these are songs ABOUT Sri
    // Swamiji — written in his praise — not songs addressed to him.
    headline: { lead: 'Songs about Sri Swamiji,', accent: 'and other timeless bhajans.' },
    lede:
      'Beloved bhajans by various artists — songs about Sri Swamiji and timeless devotional ' +
      'favourites, gathered in one place and downloaded onto your phone to read, chant and sing.',
    stats: [
      { value: '100+', label: 'bhajans' },
      { value: '5', label: 'composition languages' },
      { value: '10', label: 'reading languages' },
      { value: '8', label: 'deities' },
    ],
    // No rating: the listing has 2 ratings. A "5.0" off two votes would be
    // technically true and practically meaningless. Add one when it means something.
    shots: [
      { src: '/img/app/home.webp', alt: 'The SGS Satsang home screen, with a Browse button', caption: 'Home' },
      { src: '/img/app/bhajan.webp', alt: 'A bhajan showing its composer and the note that it was published with Sri Swamiji’s permission, above the lyrics', caption: 'A bhajan' },
      { src: '/img/app/browse.webp', alt: 'The browse screen: Instant Bhajan Finder, the whole library, Recently Viewed, Recently Added, Deities and Composition Languages', caption: 'Browse' },
      { src: '/img/app/details.webp', alt: 'A bhajan’s details: its deity, its composition language and its composer', caption: 'Details' },
      { src: '/img/app/languages.webp', alt: 'The languages screen, showing downloaded languages and others available to download', caption: 'Languages' },
    ],
    features: {
      heading: 'What the app does that this website can’t',
      lede: 'The site is a window onto the collection. The app hands you the collection.',
      items: [
        {
          icon: 'offline',
          title: 'It works with no signal',
          body:
            'The whole collection downloads onto your phone. In a hall, on a flight, mid-commute: ' +
            'it opens instantly and it opens completely.',
        },
        {
          icon: 'script',
          title: 'Ten languages, each in its own script',
          body:
            'Read in Telugu, Kannada, Hindi, Tamil and more, with the transliteration underneath. ' +
            'Download only the languages you want and keep them.',
        },
        {
          icon: 'search',
          title: 'Browse your way',
          body:
            'By deity, by composer, or by the language a bhajan was composed in — Telugu, ' +
            'Saṁskṛtaṁ, Hindī, Kannaḍa, English. Jump straight to an id with the Instant Bhajan ' +
            'Finder, or search across every script at once.',
        },
        {
          icon: 'list',
          title: 'Picks up where you left off',
          body:
            'Recently Viewed and Recently Added keep the thread of your practice, so returning to ' +
            'last night’s bhajan takes one tap.',
        },
      ],
    },
    tablet: {
      src: '/img/app/ipad.webp',
      alt: 'SGS Satsang on iPad, showing the Instant Bhajan Finder and browse tiles for deities, composition languages, ragams and composers',
      heading: 'Bigger on iPad — and it runs on your Mac',
      body:
        'The same collection, laid out for a larger screen. On an Apple silicon Mac it installs ' +
        'straight from the App Store and runs as a desktop app.',
    },
    languages: {
      heading: 'Read in your own script',
      body:
        'Pick the languages you read. Each one downloads as its own pack and stays on the device — ' +
        'switch between them mid-bhajan without losing your place.',
      endonyms: ['తెలుగు', 'ಕನ್ನಡ', 'हिन्दी', 'தமிழ்', 'മലയാളം', 'বাংলা', 'ଓଡ଼ିଆ', 'ਪੰਜਾਬੀ', 'ગુજરાતી', 'English'],
    },
    closing: {
      heading: 'Take it with you',
      body: 'Listen, chant, learn, and sing the glory of Guru and God.',
    },
  },
};
